/**
 * Auth Context
 * Manages the JWT token, user session, and login/logout.
 * Token + user are kept in sessionStorage (cleared when the browser closes).
 *
 * Phase 5: proactive token refresh + graceful recovery.
 *  - After login / on restore, a timer is scheduled to refresh the access token
 *    ~1 minute before it expires (exp decoded from the JWT, so a page reload
 *    reschedules correctly instead of assuming a full hour remains).
 *  - `refreshToken()` is also called on-demand by the API client when a request
 *    hits a 401, so an expired token is silently swapped and the request retried.
 *  - If a refresh fails, the session is torn down (logout) rather than left in a
 *    broken half-authenticated state.
 */

import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabaseClient } from '../services/supabase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'rep';
  company_id: string;
  timezone?: string; // IANA zone (N2b); default 'America/Toronto'
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<string | null>;
  updateUser: (patch: Partial<User>) => void;
  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Milliseconds until we should refresh a given access token: 1 minute before its
 * `exp`, clamped to >= 0. Falls back to 50 minutes if the token can't be decoded
 * (Supabase default lifetime is 1 hour).
 */
function msUntilRefresh(accessToken: string): number {
  const FALLBACK = 50 * 60 * 1000;
  const BUFFER = 60 * 1000;
  try {
    const payload = accessToken.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof json.exp !== 'number') return FALLBACK;
    return Math.max(0, json.exp * 1000 - Date.now() - BUFFER);
  } catch {
    return FALLBACK;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lets scheduleRefresh call the latest refreshToken without a circular dep.
  const refreshFnRef = useRef<() => Promise<string | null>>();

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (accessToken: string) => {
      clearRefreshTimer();
      refreshTimer.current = setTimeout(() => {
        refreshFnRef.current?.();
      }, msUntilRefresh(accessToken));
    },
    [clearRefreshTimer]
  );

  const logout = useCallback(() => {
    clearRefreshTimer();
    sessionStorage.removeItem('mf_token');
    sessionStorage.removeItem('mf_user');
    setToken(null);
    setUser(null);
    setError(null);
    supabaseClient.auth.signOut();
  }, [clearRefreshTimer]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data, error: refreshError } = await supabaseClient.auth.refreshSession();
      if (refreshError) throw refreshError;

      const newToken = data.session?.access_token;
      if (!newToken) throw new Error('No new token returned');

      sessionStorage.setItem('mf_token', newToken);
      setToken(newToken);
      scheduleRefresh(newToken);
      return newToken;
    } catch {
      // Refresh failed — the session is unrecoverable, so end it cleanly.
      logout();
      return null;
    }
  }, [scheduleRefresh, logout]);

  // Keep the ref pointing at the current refreshToken for scheduled callbacks.
  useEffect(() => {
    refreshFnRef.current = refreshToken;
  }, [refreshToken]);

  // Restore any existing session from sessionStorage on mount.
  useEffect(() => {
    const savedToken = sessionStorage.getItem('mf_token');
    const savedUser = sessionStorage.getItem('mf_user');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        scheduleRefresh(savedToken);
      } catch {
        sessionStorage.removeItem('mf_token');
        sessionStorage.removeItem('mf_user');
      }
    }

    setLoading(false);
    return clearRefreshTimer;
    // Runs once on mount; scheduleRefresh/clearRefreshTimer are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Authenticate with Supabase.
      const { data, error: authError } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      const accessToken = data.session?.access_token;
      const authUserId = data.user?.id;
      if (!accessToken || !authUserId) throw new Error('No access token returned');

      // 2. Fetch THIS user's record from the backend by id (admins see many
      //    users, so we can't assume the first row is the current user).
      const response = await fetch(`${API_BASE_URL}/api/users/${authUserId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to load user profile');

      const payload = await response.json();
      const currentUser = payload.data as User;
      if (!currentUser) throw new Error('User profile not found');

      // 3. Persist for the session and schedule a refresh before expiry.
      sessionStorage.setItem('mf_token', accessToken);
      sessionStorage.setItem('mf_user', JSON.stringify(currentUser));

      setToken(accessToken);
      setUser(currentUser);
      scheduleRefresh(accessToken);
    } catch (err: any) {
      const msg = err?.message || 'Login failed';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Merge a partial update into the current user (e.g. timezone) so preference
  // changes re-render immediately and survive a same-session reload.
  const updateUser = useCallback((patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      sessionStorage.setItem('mf_user', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        loading,
        error,
        login,
        logout,
        refreshToken,
        updateUser,
        isAuthenticated: !!token && !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
